/**
 * Postman component, to be used throughout the project for send requests over HTTP.
 * Based on axios npm module: https://github.com/mzabriskie/axios
 *
 * Usage: https://github.com/mzabriskie/axios
 *
 * IFDO: PostmanConfig class for config object. Pros: readable, Cons will need
 *       updates constantly to expose axios config features that will be encapsulated.
 *
 * Extend this component if further customization is needed later down the road.
 */
import { AuviousError } from "./AuviousError";
// import { Promise } from "es6-promise";
import axios from "axios";
import { Util } from "./Util";
import { OAuth2Client } from "./auth";
export class Postman {
    get csrfToken() {
        return this._csrfToken;
    }
    get csrfCookie() {
        return Postman.csrfCookieName + "=" + this._csrfToken;
    }
    constructor(config = {}) {
        this._csrfToken = Util.uuidgen();
        this._refreshRequested = config.onRefreshToken;
        this._refreshTokenExpired = config.onRefreshTokenExpired;
        this._tokenRefreshRoute = config.tokenRefreshRoute;
        // set the cookie and axios' related config to append the csrf token in the headers.
        if (typeof document !== "undefined") {
            document.cookie = this.csrfCookie + ";path=/";
            config.xsrfCookieName = Postman.csrfCookieName;
            config.xsrfHeaderName = Postman.csrfHeaderName;
        }
        this._http = axios.create(config);
        // interceptor for expired token
        /*
            this._http.interceptors.request.use(
                (conf) => {
                    return conf;
                },
                (error) => {
                    Promise.reject(error);
                }
            );
            */
        // interceptor for 401 response
        this._http.interceptors.response.use((response) => response, (error) => {
            const originalRequest = error.config;
            if (error.response &&
                error.response.status === 401 &&
                !originalRequest._retry &&
                !originalRequest.url.includes(OAuth2Client.OAUTH2_TOKEN_ROUTE) &&
                !originalRequest.url.includes(OAuth2Client.TOKEN_REFRESH_ROUTE)) {
                originalRequest._retry = true;
                if (this._refreshRequested) {
                    const tokenRefresh = new Promise((resolve, reject) => {
                        this._refreshRequested()
                            .then((success) => {
                            originalRequest.headers.authorization =
                                "Bearer " + success.accessToken;
                            resolve(originalRequest);
                        })
                            .catch((err) => reject(err));
                    });
                    return tokenRefresh
                        .then((retryRequest) => axios(retryRequest))
                        .catch((err) => Promise.reject(err));
                }
            }
            else {
                if (error.response &&
                    error.response.status === 401 &&
                    this._refreshTokenExpired) {
                    this._refreshTokenExpired();
                }
                else {
                    return Promise.reject(error);
                }
            }
            return Promise.reject(error);
        });
    }
    /**
     * set csrf related headers manually when document is not available current platform.
     */
    appendCsrfHeaders(config) {
        if (!config.headers) {
            config.headers = {};
        }
        if (typeof document === "undefined") {
            config.headers[Postman.csrfHeaderName] = this.csrfToken;
            if (config.headers.cookie) {
                config.headers.cookie += "; " + this.csrfCookie;
            }
            else {
                config.headers.cookie = this.csrfCookie;
            }
        }
        else {
            config.headers[Postman.csrfHeaderName] = this.csrfToken;
            config.headers[Postman.csrfAppCookieName] = this.csrfCookie;
        }
    }
    request(config = {}) {
        this.appendCsrfHeaders(config);
        return new Promise((resolve, reject) => {
            this._http
                .request(config)
                .then((res) => {
                // response schema:
                //
                // {
                //   // `data` is the response that was provided by the server
                //   data: {},
                //   // `status` is the HTTP status code from the server response
                //   status: 200,
                //   // `statusText` is the HTTP status message from the server response
                //   statusText: 'OK',
                //   // `headers` the headers that the server responded with
                //   // All header names are lower cased
                //   headers: {},
                //   // `config` is the config that was provided to `axios` for the request
                //   config: {},
                //   // `request` is the request that generated this response
                //   // It is the last ClientRequest instance in node.js (in redirects)
                //   // and an XMLHttpRequest instance the browser
                //   request: {}
                // }
                resolve(res);
            })
                .catch((err) => {
                var _a, _b, _c, _d, _e;
                if (err.response) {
                    err.data = AuviousError.create(((_a = err.response.data) === null || _a === void 0 ? void 0 : _a.message) ||
                        ((_b = err.response.data) === null || _b === void 0 ? void 0 : _b.error_description) ||
                        "No error description on response received", ((_c = err.response.data) === null || _c === void 0 ? void 0 : _c.error_description) ||
                        ((_d = err.response.data) === null || _d === void 0 ? void 0 : _d.code) ||
                        err.code ||
                        err.response.statusText, ((_e = err.response.data) === null || _e === void 0 ? void 0 : _e.status) || err.response.statusText);
                }
                else if (err.request) {
                    err.data = AuviousError.create("The request was made but no response was received.", err.code, "Network Error");
                }
                else {
                    // on manual cancellations
                    err.data = new AuviousError({
                        message: err.message || "Failed to send request",
                    });
                }
                err.name = "Error";
                reject(err);
            });
        });
    }
    get(url, config = {}) {
        config.method = Postman.GET;
        config.url = url;
        return this.request(config);
    }
    post(url, body, config = {}) {
        config.method = Postman.POST;
        config.url = url;
        config.data = body;
        return this.request(config);
    }
    put(url, body, config = {}) {
        config.method = Postman.PUT;
        config.url = url;
        config.data = body;
        return this.request(config);
    }
    delete(url, config = {}) {
        config.method = Postman.DELETE;
        config.url = url;
        return this.request(config);
    }
}
Postman.GET = "get";
Postman.POST = "post";
Postman.PUT = "put";
Postman.DELETE = "delete";
Postman.csrfCookieName = "CSRF-TOKEN";
Postman.csrfHeaderName = "X-CSRF-TOKEN";
Postman.csrfAppCookieName = "X-AUVIOUS-COOKIE";
